import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, Pause, Play, SlidersHorizontal } from "lucide-react";
import { getMe, definirTarefaAtual } from "../services/me";

const INTERVALO_MS = 60 * 60 * 1000; // lembrete a cada 1h (pedido da Rec, jul/2026 — antes 2h)

/**
 * Pop-up BLOQUEANTE de "você ainda está nessa?" (PDF Fase 02 — Relatórios #6).
 * Aparece por cima de tudo a cada 1h enquanto o play (tarefa atual) segue ligado,
 * interrompendo a pessoa pra confirmar que ainda está trabalhando, pausar ou
 * ajustar o tempo. Cadência é client-side: a cada bloco de 1h desde o início do
 * foco, reaparece até ser dispensado naquele bloco.
 */
export function LembretePlayBloqueante() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe, refetchInterval: 60000 });
  const [, setTick] = useState(0);
  // Último bloco de 2h dispensado, por sessão de foco (key = tarefa+início).
  const [dispensado, setDispensado] = useState<{ key: string; bloco: number } | null>(null);

  const temPlay = !!me?.tarefaAtual && !!me?.tarefaAtualDesde;

  // Recomputa o tempo decorrido a cada 30s — só enquanto há play ligado (evita
  // re-render perpétuo quando o usuário está sem play, que é o caso comum).
  useEffect(() => {
    if (!temPlay) return;
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, [temPlay]);

  const pausarM = useMutation({
    mutationFn: () => definirTarefaAtual(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["tarefas"] });
    },
    onError: () => {
      // Não deixa o modal bloqueante travado se a pausa falhar: dispensa o bloco
      // atual (some até o próximo) e avisa pra tentar pelo card.
      if (me?.tarefaAtual && me?.tarefaAtualDesde) {
        const k = `${me.tarefaAtual.id}:${me.tarefaAtualDesde}`;
        const b = Math.max(1, Math.floor((Date.now() - new Date(me.tarefaAtualDesde).getTime()) / INTERVALO_MS));
        setDispensado({ key: k, bloco: b });
      }
      alert("Não foi possível pausar agora. Tente de novo pelo botão de play do card.");
    },
  });

  const tarefa = me?.tarefaAtual;
  const desde = me?.tarefaAtualDesde;
  if (!tarefa || !desde) return null;

  const key = `${tarefa.id}:${desde}`;
  const decorridoMs = Math.max(0, Date.now() - new Date(desde).getTime()); // clamp p/ relógio adiantado
  const bloco = Math.floor(decorridoMs / INTERVALO_MS);
  const jaDispensou = dispensado && dispensado.key === key && dispensado.bloco >= bloco;
  const mostrar = bloco >= 1 && !jaDispensou && !pausarM.isPending;
  if (!mostrar) return null;

  const horas = Math.floor(decorridoMs / 3600000);
  const minutos = Math.round((decorridoMs % 3600000) / 60000);
  const tempoTxt = horas > 0 ? `${horas}h${String(minutos).padStart(2, "0")}` : `${minutos}min`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border-2 border-amber-300 dark:border-amber-700 overflow-hidden">
        <div className="bg-amber-50 dark:bg-amber-900/30 px-6 py-4 flex items-center gap-3 border-b border-amber-200 dark:border-amber-800">
          <div className="w-11 h-11 rounded-full bg-amber-100 dark:bg-amber-800/50 flex items-center justify-center shrink-0">
            <Clock size={22} className="text-amber-600 dark:text-amber-300" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Você ainda está nessa tarefa?</h2>
            <p className="text-xs text-amber-700 dark:text-amber-300">Play ligado há {tempoTxt}</p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Você está com o play ligado em{" "}
            <strong className="text-slate-800 dark:text-slate-100">{tarefa.titulo}</strong>
            {tarefa.projeto?.nome && <> ({tarefa.projeto.nome})</>} há {tempoTxt}.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Se já parou, pause o play pra não contar tempo a mais. Se segue trabalhando, é só continuar.
          </p>
        </div>
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/40 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => pausarM.mutate()}
              disabled={pausarM.isPending}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
            >
              <Pause size={16} /> Parei — pausar play
            </button>
            <button
              onClick={() => setDispensado({ key, bloco })}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Play size={16} /> Continuar trabalhando
            </button>
          </div>
          <button
            onClick={() => { setDispensado({ key, bloco }); navigate(`/tarefas?tarefa=${tarefa.id}`); }}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50"
          >
            <SlidersHorizontal size={14} /> Trabalhei um tempo diferente — ajustar tempo
          </button>
        </div>
      </div>
    </div>
  );
}
